import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api/axios';
import './HybridFlowConfigurator.css';

const BRANCH_LABELS = {
  true: 'Ja',
  false: 'Nee',
  accepted: 'Geaccepteerd',
  notAccepted: 'Niet geaccepteerd',
  response: 'Reactie',
  noResponse: 'Geen reactie',
  out: 'Volgende stap',
};

const MODE_LABELS = {
  manual: 'Manual',
  hybrid: 'Hybrid',
  agent: 'AI Agent',
};

function slotKey(slot) {
  return `${slot.sourceNodeId}:${slot.sourceHandle || 'out'}`;
}

function HandoffRoute({ slot, active = false }) {
  return <div className={`hybrid-flow-route ${active ? 'active' : ''}`} aria-label={`Route vanaf ${slot.sourceLabel || 'workflowstap'}`}>
    <span className="hybrid-flow-route-node manual">{slot.sourceLabel || 'Workflowstap'}</span>
    <b>→</b>
    <span className="hybrid-flow-route-branch">{BRANCH_LABELS[slot.sourceHandle] || slot.sourceHandle || 'Volgende stap'}</span>
    <b>→</b>
    <span className="hybrid-flow-route-node agent">AI Agent</span>
  </div>;
}

export default function HybridFlowConfigurator() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [plannerStatus, setPlannerStatus] = useState(null);
  const [busyKey, setBusyKey] = useState('');
  const [moveTargets, setMoveTargets] = useState({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const [workflowResponse, plannerResponse] = await Promise.all([
        api.get(`/agent/campaigns/${id}/hybrid-workflow`),
        api.get(`/agent/campaigns/${id}/planner-status`).catch(() => ({ data: null })),
      ]);
      setData(workflowResponse.data || null);
      setPlannerStatus(plannerResponse.data || null);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addHandoff = async (slot) => {
    const key = `add:${slotKey(slot)}`;
    setBusyKey(key); setError(''); setNotice('');
    try {
      await api.post(`/agent/campaigns/${id}/hybrid-workflow/handoffs`, {
        sourceNodeId: slot.sourceNodeId,
        sourceHandle: slot.sourceHandle,
      });
      setNotice(`AI Agent-handoff toegevoegd na ${slot.sourceLabel} · ${BRANCH_LABELS[slot.sourceHandle] || slot.sourceHandle}.`);
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally {
      setBusyKey('');
    }
  };

  const removeHandoff = async (handoff) => {
    if (!window.confirm('Deze AI-handoff verwijderen? Het betreffende workflowpad wordt daarna weer handmatig totdat je een nieuwe eindstap toevoegt.')) return;
    const key = `remove:${handoff.nodeId}`;
    setBusyKey(key); setError(''); setNotice('');
    try {
      await api.delete(`/agent/campaigns/${id}/hybrid-workflow/handoffs/${encodeURIComponent(handoff.nodeId)}`);
      setNotice('AI Agent-handoff verwijderd.');
      setMoveTargets((current) => {
        const next = { ...current };
        delete next[handoff.nodeId];
        return next;
      });
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally {
      setBusyKey('');
    }
  };

  const moveHandoff = async (handoff, slots) => {
    const selected = moveTargets[handoff.nodeId];
    const target = slots.find((slot) => slotKey(slot) === selected);
    if (!target) return;
    const key = `move:${handoff.nodeId}`;
    setBusyKey(key); setError(''); setNotice('');
    try {
      await api.post(`/agent/campaigns/${id}/hybrid-workflow/handoffs/${encodeURIComponent(handoff.nodeId)}/move`, {
        sourceNodeId: target.sourceNodeId,
        sourceHandle: target.sourceHandle,
      });
      setNotice(`AI Agent-handoff verplaatst naar ${target.sourceLabel} · ${BRANCH_LABELS[target.sourceHandle] || target.sourceHandle}.`);
      setMoveTargets((current) => ({ ...current, [handoff.nodeId]: '' }));
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally {
      setBusyKey('');
    }
  };

  const validation = data?.validation;
  const slots = validation?.handoffSlots || [];
  const handoffs = validation?.handoffs || [];
  const editable = String(data?.campaignStatus || '').toLowerCase() === 'concept';
  const runtimeMode = plannerStatus?.mode || data?.mode || (validation?.hybrid ? 'hybrid' : 'manual');
  const eligibleLeadCount = Number(plannerStatus?.eligibleLeadCount || 0);
  const totalLeadCount = Number(plannerStatus?.totalLeadCount || 0);

  return <main className="hybrid-flow-page">
    <header className="hybrid-flow-header">
      <div>
        <Link to={`/campaigns/${id}`} className="hybrid-flow-back">← Terug naar campagne</Link>
        <p className="hybrid-flow-kicker">Hybrid Flow</p>
        <h1>Handmatige flow → AI Agent</h1>
        <p>Kies het punt waar de vaste workflow eindigt. Vanaf daar plant de AI Agent per lead dynamisch de volgende stap; outbound blijft approval-gated.</p>
      </div>
      <Link to={`/campaigns/${id}/agent`} className="hybrid-flow-button secondary">Open AI Agent</Link>
    </header>

    {notice && <div className="hybrid-flow-notice">{notice}</div>}
    {error && <div className="hybrid-flow-error">{error}</div>}

    {!data ? <section className="hybrid-flow-card">Workflow inspecteren…</section> : <>
      <section className="hybrid-flow-summary">
        <div><span>Campagnestatus</span><strong>{data.campaignStatus || '—'}</strong></div>
        <div><span>Runtime-modus</span><strong>{MODE_LABELS[runtimeMode] || runtimeMode}</strong></div>
        <div><span>AI-bevoegde leads</span><strong>{eligibleLeadCount}/{totalLeadCount || 0}</strong></div>
        <div><span>Agent-handoffs</span><strong>{validation?.handoffCount || 0}</strong></div>
      </section>

      <div className={`hybrid-flow-mode-banner mode-${runtimeMode}`}>
        <strong>{MODE_LABELS[runtimeMode] || runtimeMode}</strong>
        {runtimeMode === 'manual' && <span>De AI Planner mag geen leads benaderen. De handmatige workflow blijft volledig leidend.</span>}
        {runtimeMode === 'hybrid' && <span>Alleen leads die daadwerkelijk een Hand over to Agent-stap hebben bereikt, worden door de AI Planner verwerkt.</span>}
        {runtimeMode === 'agent' && <span>De AI Planner is campagnebreed actief. Alle geschikte leads vallen binnen Agent Mode.</span>}
      </div>

      {!editable && <div className="hybrid-flow-warning">De workflow is alleen aanpasbaar zolang de campagne Concept is. Bestaande handoffs blijven wel actief in de campagne.</div>}
      {validation?.errors?.length > 0 && <div className="hybrid-flow-error"><strong>Workflow bevat fouten</strong>{validation.errors.map((item) => <p key={`${item.code}-${item.nodeId || item.edgeId || ''}`}>{item.message}</p>)}</div>}

      {handoffs.length > 0 && <section className="hybrid-flow-card">
        <div className="hybrid-flow-card-head"><div><h2>Actieve overdrachtspunten</h2><p>Deze routes eindigen al bij de AI Agent. Je kunt een handoff in Concept verplaatsen of verwijderen; de backend wijzigt de graph atomisch.</p></div></div>
        <div className="hybrid-flow-existing">
          {handoffs.map((handoff) => {
            const moveKey = `move:${handoff.nodeId}`;
            const removeKey = `remove:${handoff.nodeId}`;
            return <article key={handoff.nodeId} className="hybrid-flow-existing-item">
              <HandoffRoute slot={handoff} active />
              <small>{handoff.reachable ? `Handoff-node ${handoff.nodeId}` : 'Niet verbonden handoff'}</small>
              {editable && <div className="hybrid-flow-handoff-actions">
                <select
                  value={moveTargets[handoff.nodeId] || ''}
                  onChange={(event) => setMoveTargets((current) => ({ ...current, [handoff.nodeId]: event.target.value }))}
                  disabled={!slots.length || Boolean(busyKey)}
                  aria-label="Nieuw overdrachtspunt"
                >
                  <option value="">Verplaats naar…</option>
                  {slots.map((slot) => <option key={`${handoff.nodeId}-${slotKey(slot)}`} value={slotKey(slot)}>{slot.sourceLabel} · {BRANCH_LABELS[slot.sourceHandle] || slot.sourceHandle}</option>)}
                </select>
                <button className="hybrid-flow-button secondary" disabled={!moveTargets[handoff.nodeId] || Boolean(busyKey)} onClick={() => moveHandoff(handoff, slots)}>{busyKey === moveKey ? 'Verplaatsen…' : 'Verplaatsen'}</button>
                <button className="hybrid-flow-button danger" disabled={Boolean(busyKey)} onClick={() => removeHandoff(handoff)}>{busyKey === removeKey ? 'Verwijderen…' : 'Verwijderen'}</button>
              </div>}
            </article>;
          })}
        </div>
      </section>}

      <section className="hybrid-flow-card">
        <div className="hybrid-flow-card-head"><div><h2>Beschikbare overdrachtspunten</h2><p>Iedere kaart is een vrije uitgang in de bestaande workflow. De backend controleert opnieuw of het slot nog vrij en geldig is wanneer je de handoff toevoegt.</p></div><button className="hybrid-flow-button secondary" onClick={load} disabled={Boolean(busyKey)}>Vernieuwen</button></div>
        {slots.length === 0 ? <p className="hybrid-flow-empty">Geen vrije workflow-uitgangen beschikbaar. Voeg in de handmatige Flow Builder eerst een pad toe of verwijder een bestaande eindstap.</p> : <div className="hybrid-flow-slots">
          {slots.map((slot) => {
            const key = `add:${slotKey(slot)}`;
            return <article key={slotKey(slot)} className="hybrid-flow-slot">
              <div className="hybrid-flow-slot-main">
                <HandoffRoute slot={slot} />
                <small>Bronnode {slot.sourceNodeId}</small>
              </div>
              <button className="hybrid-flow-button" disabled={!editable || Boolean(busyKey)} onClick={() => addHandoff(slot)}>{busyKey === key ? 'Toevoegen…' : 'AI Agent neemt hier over'}</button>
            </article>;
          })}
        </div>}
      </section>

      <section className="hybrid-flow-card hybrid-flow-explain">
        <h2>Wat gebeurt er bij deze stap?</h2>
        <div className="hybrid-flow-chain"><span>Vaste workflow</span><b>→</b><span className="agent">Hand over to Agent</span><b>→</b><span>Planner</span><b>→</b><span>Approval</span><b>→</b><span>Veilige uitvoering</span></div>
        <p>De handoff zelf verstuurt niets. Hij activeert alleen doelgestuurde planning voor die specifieke lead. Een LinkedIn- of e-mailactie blijft door dezelfde approval- en execution-guards gaan.</p>
      </section>
    </>}
  </main>;
}
